import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FeatureToggleService, GlobalEventBusService } from '@nx-fe/services';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, first, takeUntil } from 'rxjs/operators';
import {
  RemoteControlEventArgs,
  RemoteControlService,
} from 'xsmodules/remote-control/services/remote-control.service';
import { IDeviceScreenTransformService } from '../../../remote-control-ng';
import { XsI18nPipe } from '@nx-fe/pipes';
import { GlobalEvents } from 'app/core/global-event-constants';
import { ZoomControllerService } from 'xsmodules/remote-control/services/zoom-controller.service';
import {
  WorkPanelBarItem,
  WorkPanelBarItemType,
} from 'xsmodules/ticket/components/work-panel/models/work-panel-bar-items';
import { DisplayMode } from 'xsmodules/remote-control/remote-control-view/toolbar-popover/toolbar-popover.component';
import { DeviceScale, ScaleSize } from 'app/modules/remote-control-ng/definitions';
import { RestartThrottling } from 'app/modules/remote-control-ng/services/restartThrottling';
import { RcContext } from 'app/modules/remote-control-ng/context/rcContext';
import { DeviceContext, IRcDeviceContext } from 'app/modules/remote-control-ng/context/deviceContext';
import { PanService } from 'app/modules/remote-control-ng/services/panModeService';
import { DeviceStateRegistryService, RemoteControlDeviceState } from 'app/modules/remote-control/services/device-state-registry.service';

@Component({
  selector: 'app-rc-zoom',
  templateUrl: './rc-zoom.component.html',
  styleUrls: ['./rc-zoom.component.scss'],
})
export class RcZoomComponent implements OnInit, OnDestroy {
  @Input() rcState: RemoteControlDeviceState;

  public get isRecording() {
    if (this.rcState && this.rcState.recording) {
      return this.rcState.recording;
    }
    return false;
  }

  public remoteControlStartedUnbind: Function;
  public remoteControlEndedUnbind: Function;
  public remoteControlTransferUnbind: Function;
  public remoteControlMinimizeEventUnbind: Function;
  public rcBarRestoredSub: Subscription;
  public associationBarRestoredSub: Subscription;
  public associationBarMinimizedSub: Subscription;
  public remoteControlTabSelectedSub: Subscription;
  public mutationObserver: any; // ResizeObserver;
  public autoZoomed: boolean;

  private currentScale: number;
  private restartScale: number;
  private canvas: any;
  private outerCanvasWidth: number;
  private outerCanvasHeight: number;
  private disableRestarts = false;
  private videoStreaming = false;

  private subscriptions: Subscription[] = [];

  private zoomInButtonName = 'zoom-in';
  private zoomOutButtonName = 'zoom-out';
  private fitResolutionButtonName = 'fit-resolution';
  private fitScreenButtonName = 'fit-screen';

  public displayMode = DisplayMode;
  public collapsedButtonItem: WorkPanelBarItem;
  public expandedButtonItems: WorkPanelBarItem[];

  onDestroy$ = new Subject<void>();
  private onVideoStreamChangeSub: Subscription;
  constructor(
    private saI18nPipe: XsI18nPipe,
    private zoomControllerService: ZoomControllerService,
    private featureToggleService: FeatureToggleService,
    private remoteControlService: RemoteControlService,
    private globalEventBusService: GlobalEventBusService,
    private restartThrottle: RestartThrottling,
    private panService: PanService,
    private deviceStateRegistry: DeviceStateRegistryService,
  ) {
    // this.panEnabled = false;
    this.setAutoZoomActive();

    this.canvas = null;
    this.outerCanvasWidth = -1;
    this.outerCanvasHeight = -1;
    this.currentScale = 1;
  }

  private get transformService(): IDeviceScreenTransformService {
    return RcContext.instance.deviceScreenTransformService;
  }

  ngOnInit() {
    this.buildRcMenuButtonItems();

    // $(window).bind('resize', this.onResize);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this.mutationObserver = new (window as any).ResizeObserver(this.onResize);
    const config = {
      attributes: true,
      subtree: true,
      attributeFilter: ['width', 'height'],
    };

    const remoteControlViewsArea = document.getElementById('remoteControlViewsArea');
    if (remoteControlViewsArea) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.mutationObserver.observe(remoteControlViewsArea, config);
    }

    this.restoreState();

    this.subscriptions.push(this.globalEventBusService.register(GlobalEvents.rcBarRestored, this.onResize));

    this.subscriptions.push(this.globalEventBusService.register(GlobalEvents.associationBarRestored, this.onResize));

    this.subscriptions.push(
      this.globalEventBusService.register(GlobalEvents.associationBarMinimized, () =>
        setTimeout(() => {
          this.onResize();
        }, 0)
      )
    );

    this.subscriptions.push(
      this.globalEventBusService.register(GlobalEvents.remoteControlTabSelected, () =>
        setTimeout(() => {
          this.onRemoteControlTabSelectedSub();
        }, 0)
      )
    );

    this.remoteControlService.remoteControlStarted$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.onRemotecontrolStarted(true));

    this.remoteControlService.remoteControlEnded$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.onRemoteControlEnded());

    this.remoteControlService.remoteControlTransfer$.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.transfer());

    RcContext.instance.multiScreen.remoteControlDisplayChanged$.pipe(takeUntil(this.onDestroy$)).subscribe(monitorId => {
      this.zoom(0, true);
      this.pan(false);
    });


    this.subscriptions.push(
      this.globalEventBusService.register(GlobalEvents.remoteControlMaximized, () => {
        setTimeout(() => this.doResize(false), 0);
      })
    );

    this.subscriptions.push(
      this.globalEventBusService.register(
        GlobalEvents.remoteControlCompleteTransfer,
        (completed: boolean) => {
          if (completed) {
            this.completeTransfer();
            this.onRemotecontrolStarted(false);
          }
        },
        true
      )
    );

    // the controller might be distructed and restructed when the underly component is destroyed and recreated
    this.videoStreaming = this.remoteControlService.videoStreamingSupportedForCurrentDevice;
    this.setAutoZoomActive();
    this.subscribeOnVideoStreamUpdate();
    this.subscriptions.push(
      this.remoteControlService.RemoteControlStarted.subscribe((status: RemoteControlEventArgs) => {
        this.videoStreaming = status.isStreamingRC;
        this.setAutoZoomActive();
        this.subscribeOnVideoStreamUpdate();
      })
    );
  }

  private subscribeOnVideoStreamUpdate = (): void => {
    if (!this.videoStreaming || !!this.onVideoStreamChangeSub) {
      return;
    }

    const context = this.remoteControlService.getRemoteControlContext();
    const deviceViewHandler = context ? context.deviceViewHandler : null;
    if (deviceViewHandler) {
      this.onVideoStreamChangeSub = deviceViewHandler.onUpdate$.pipe(debounceTime(100)).subscribe(() => {
        this.checkIfZoomed();
      });
    }
  };

  ngOnDestroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      delete this.mutationObserver;
    }
    if (this.onVideoStreamChangeSub) {
      this.onVideoStreamChangeSub.unsubscribe();
      delete this.onVideoStreamChangeSub;
    }
    // $(window).unbind('resize', this.onResize);
    if (this.transformService) {
      this.transformService.unsubscribeToEndOfTransformations(this.checkIfZoomed);
    }

    this.onDestroy$.next();
    this.onDestroy$.complete();

    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }

    this.saveState();
  }

  public isPanEnabled(): boolean {
    return this.panService.isPanEnabled();
  }

  public isZoomed(): boolean {
    if (!RcContext.instance.initialized) {
      return false;
    }

    if (this.videoStreaming) {
      const context = this.remoteControlService.getRemoteControlContext();
      const deviceViewHandler = context ? context.deviceViewHandler : null;
      return deviceViewHandler ? !deviceViewHandler.fullyVisible() : false;
    }

    const ctx = RcContext.instance.deviceContext as DeviceContext;
    if (ctx) {
      this.canvas = ctx.canvas?.element;
      if (this.canvas) {
        const canvasOuter = document.getElementById('canvasOuter');
        if (canvasOuter !== undefined && canvasOuter !== null && this.transformService && ctx.scaleFactor !== undefined) {
          return ctx.scaleFactor > 1;
        }
      }
    }

    return false;
  }

  private getScaleFromDeviceViewHandler(): number {
    if (this.videoStreaming) {
      const context = this.remoteControlService.getRemoteControlContext();
      const deviceViewHandler = context ? context.deviceViewHandler : null;
      return !deviceViewHandler ? -1 : deviceViewHandler.getViewScale();
    }

    return !this.transformService ? -1 : this.transformService.getCustomScale();
  }

  public canZoom(factor: number): boolean {
    const handlerScale = this.getScaleFromDeviceViewHandler();
    if (handlerScale === -1) return false;

    this.currentScale = handlerScale;

    if (factor !== 0) {
      // Zooming limits are different for streaming and standard devices, due to the
      // differences in the implementation in the RestartThrottler
      const newScale = Math.floor(this.videoStreaming ? this.currentScale * (devicePixelRatio ?? 1) : this.currentScale);
      const maxScale = this.videoStreaming ? DeviceScale.Scale100 : ScaleSize.SCALE_MAX;
      const minScale = this.videoStreaming ? ScaleSize.SCALE_MIN * (devicePixelRatio ?? 1) : ScaleSize.SCALE_MIN;
      if (factor > 0 && newScale >= maxScale && !this.autoZoomed) {
        return false;
      } else if (factor < 0 && newScale <= minScale && !this.autoZoomed) {
        return false;
      }
    }

    return true;
  }

  public zoomIdentity() {
    if (this.isRecording) {
      return;
    }

    if (this.videoStreaming) {
      const context = this.remoteControlService.getRemoteControlContext();
      const deviceViewHandler = context ? context.deviceViewHandler : null;
      if (deviceViewHandler) {
        deviceViewHandler.scale(100);
      }

      return;
    }

    if (this.transformService !== undefined && this.transformService !== null) {
      this.updateForCanvasAndGetScale();

      this.autoZoomed = false;
      this.transformService.setTargetScale(DeviceScale.ScaleCustom);
      this.transformService.setCustomScale(100 / this.transformService.getScreenToSkinRatio());
      this.sendRestartMessage(100);
      this.runDigest();
    }
  }

  // Zoom in (if factor > 0), zoom out (if factor < 0) or auto zoom (if factor === 0)
  // If factor is left undefined (e.g. calling zoom()), re-zooms back to the most recent zooming factor
  public zoom = (factor: number, force: boolean = false) => {
    if (this.isRecording) {
      return;
    }

    if (this.videoStreaming) {
      const context = this.remoteControlService.getRemoteControlContext();
      const deviceViewHandler = context ? context.deviceViewHandler : null;
      if (deviceViewHandler) {
        if (factor === ScaleSize.SCALE_RESIZE) {
          factor = undefined;
        }

        if (factor === 0 || (factor === undefined && this.autoZoomed)) {
          this.autoZoomed = true;
          deviceViewHandler.scale(0);
        } else {
          this.autoZoomed = false;
          if (factor === undefined) {
            factor = 0;
          }

          let newScale = deviceViewHandler.getViewScale(factor);
          if (newScale > ScaleSize.SCALE_MAX) {
            newScale = ScaleSize.SCALE_MAX;
          } else if (newScale < ScaleSize.SCALE_MIN) {
            newScale = ScaleSize.SCALE_MIN;
          }

          deviceViewHandler.scale(newScale, force);
        }
      }

      return;
    }

    // To be able to zoom, we need the context to be ready.
    // We will subscribe to the contextReady$ stream to run the zoom as soon as it is
    // (if context is already ready, then the call is executed instantly)
    const deviceContextHandler = RcContext.instance.deviceContextHandler;

    if (deviceContextHandler) {
      deviceContextHandler.contextReady$
        .pipe(first(isReady => isReady === true)) // receives only the first message and automatically unsubscribe once received
        .subscribe(() => {
          this.handleZoom(factor, force);
        });
    }
  }

  public handleZoom(factor: number, force: boolean) {

    if (this.transformService !== undefined && this.transformService !== null) {
      this.transformService.subscribeToEndOfTransformations(this.checkIfZoomed.bind(this));

      // For window resizing, complete transformation in one frame
      if (factor === ScaleSize.SCALE_RESIZE) {
        factor = undefined;
        force = true;
      }
      this.autoZoomed = this.transformService.isAutoZoomActive();

      if (factor === 0 || (factor === undefined && this.autoZoomed)) {
        // auto zoom
        this.transformService.setTargetScale(DeviceScale.ScaleAuto);
        const oldScale = this.transformService.getAutoZoomScale();
        const newScale = this.updateForCanvasAndGetScale();

        if (oldScale !== newScale || force) {
          this.transformService.setCustomScale(oldScale);
          this.sendRestartMessage(this.transformService.getScreenScale(), force);
        }
        this.autoZoomed = true;
      } else {
        // zoom in/out
        if (factor === undefined) {
          // Return to the most recent zoom
          factor = 0;
        }
        const oldScale = this.autoZoomed
          ? this.transformService.getAutoZoomScale()
          : this.updateForCanvasAndGetScale();

        this.autoZoomed = false;
        let newScale = oldScale + factor;
        if (newScale > ScaleSize.SCALE_MAX) {
          newScale = ScaleSize.SCALE_MAX;
        } else if (newScale < ScaleSize.SCALE_MIN) {
          newScale = ScaleSize.SCALE_MIN;
        }

        if (oldScale !== newScale || force) {
          this.transformService.setTargetScale(DeviceScale.ScaleCustom);
          this.transformService.setCustomScale(newScale);
          this.sendRestartMessage(this.transformService.getScreenScale(), force);
        }
      }
      this.runDigest();
    }
  }

  // Turn pan mode on or off
  public pan(panOn: boolean) {
    if (this.isZoomed() || this.isPanEnabled()) {
      this.panService.pan(panOn, this.zoom);
    }
  }
  // Called from Transformation Handler at the end of transformation
  public checkIfZoomed() {
    if (!this.isZoomed() && this.isPanEnabled()) {
      this.controlPanMode(false);
    } else {
      this.runDigest();
    }
  }

  // Turn pan mode on/off updating UI
  private controlPanMode = (on: boolean) => {
    this.panService.controlPanMode(on);
  };

  // Update state based on outer canvas and return current scale
  private updateForCanvasAndGetScale = (): number => {
    this.transformService.checkIfAutoZoomAndGetScale();

    const canvasOuter = this.getOuterCanvas();
    if (canvasOuter) {
      this.outerCanvasWidth = canvasOuter.clientWidth;
      this.outerCanvasHeight = canvasOuter.clientHeight;
    }

    let currentScale: number;
    if (this.autoZoomed) {
      currentScale = this.transformService.getAutoZoomScale();
    } else {
      currentScale = this.transformService.getCustomScale();
      if (currentScale === undefined) {
        this.autoZoomed = true;
        currentScale = this.transformService.getAutoZoomScale();
      }
    }

    this.currentScale = currentScale;

    if (this.canvas === null) {
      this.pan(false);
    }

    return currentScale;
  };

  // Retrieve the enclosing element
  public getOuterCanvas(): HTMLElement {
    let outer = document.getElementById('canvasOuter');
    return outer;
  }

  public sendRestartMessage(scale: number, force?: boolean) {
    const session = RcContext.instance.session;
    if (session.isConnected()) {
      this.restartScale = scale;

      // If we are poping out, there is no need to send a restart message from the old RC window
      if (this.restartScale > 0 && this.rcState && !this.rcState.poppingOut) {
        if (session.hasStarted()) {
          this.restartThrottle.send(this.restartScale, !!force, RcContext.instance);
        }
      }
      this.restartScale = DeviceScale.ScaleUnknown;
    }
  }

  private restoreState = () => {
    const id = RcContext.instance.sotiAssistDeviceId;
    if (this.zoomControllerService.getState(id) !== undefined) {
      this.panService.panEnabled = this.zoomControllerService.getState(id).panEnabled;
      this.autoZoomed = this.zoomControllerService.getState(id).autoZoomed;
    } else {
      // Reset to default
      this.panService.panEnabled = false;
      this.autoZoomed = true;
    }

    if (!this.autoZoomed) {
      const outerCanvas = this.getOuterCanvas();
      this.outerCanvasWidth = outerCanvas.clientWidth;
      this.outerCanvasHeight = outerCanvas.clientHeight;
    }

    if (this.isPanEnabled()) {
      this.pan(true);
    }

    // restoreState is called either when starting or transfering an RC session
    // In both scenarios, we want to recalculate the zoom when the device rotates
    this.updateZoomOnDeviceRotation();
  };

  public updateZoomOnDeviceRotation = () => {
    // Observe rotationChanged$ so we update scale when the device rotates
    const deviceContextHandler = RcContext.instance.deviceContextHandler;
    if (deviceContextHandler) {
      deviceContextHandler.rotationChanged$.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        this.doResize(true);
      });
    }
  };

  public doResize = (forceZoom: boolean): void => {
    if (this.disableRestarts || (this.rcState && !this.rcState.firstScreenLoaded)) {
      return;
    }

    if (this.deviceStateRegistry.getCurrentState()) {
      // connected
      this.remoteControlService.setCanvasOuterHeight();
    } else {
      // not connected, in error state
      this.remoteControlService.setErrorPageHeight();
    }

    const deviceContextHandler = RcContext.instance.deviceContextHandler;
    if (deviceContextHandler) {
      const canvasOuter = document.getElementById('canvasOuter');
      if (!!canvasOuter) {
        deviceContextHandler.updateWithCanvasChanges(canvasOuter.clientWidth, canvasOuter.clientHeight);
      }
    }

    if (this.isResized() || forceZoom) {
      this.zoom(ScaleSize.SCALE_RESIZE);
    }

    if (this.transformService) {
      this.transformService.refreshCanvas();
    }
  };

  public isResized(): boolean {
    const outerCanvas = document.getElementById('canvasOuter');

    // If there is no canvasOuter yet...
    if (!outerCanvas) {
      return false;
    }

    // If canvasOuter is invisible...
    if (this.outerCanvasWidth !== outerCanvas.clientWidth || this.outerCanvasHeight !== outerCanvas.clientHeight) {
      this.outerCanvasWidth = outerCanvas.clientWidth;
      this.outerCanvasWidth = outerCanvas.clientHeight;
      return true;
    }

    return false;
  }

  public saveState() {
    this.currentScale = this.getScaleFromDeviceViewHandler();
    if (this.currentScale === -1) {
      this.currentScale = 100;
    }
    this.zoomControllerService.setState( { panEnabled: this.isPanEnabled(), autoZoomed: this.autoZoomed, remoteControlEndedUnbind: this.remoteControlEndedUnbind }, RcContext.instance.sotiAssistDeviceId);
  }

  public transfer() {
    if (this.featureToggleService.isEnabled('RemoteControlTransferCanvas')) {
      this.disableRestarts = true;
    }
    this.saveState();
    this.disableRestarts = false;
  }

  public completeTransfer() {

    const id = RcContext.instance.sotiAssistDeviceId;
    if (this.zoomControllerService.getState(id) !== undefined) {
      this.panService.panEnabled = this.zoomControllerService.getState(id).panEnabled;
      this.autoZoomed = this.zoomControllerService.getState(id).autoZoomed;
    }

    if (!this.autoZoomed) {
      const outerCanvas = this.getOuterCanvas();
      this.outerCanvasWidth = outerCanvas.clientWidth;
      this.outerCanvasHeight = outerCanvas.clientHeight;
    }

    if (this.isPanEnabled()) {
      this.pan(true);
    }
  }

  public onResize = (): void => {
    this.doResize(false);
  };

  private onRemoteControlTabSelectedSub(): void {
    // after restoring RC tab we may have missed device rotation,
    // so applying reapplying zoom regardless of the isResize() result
    this.doResize(true);
  }

  private onRemoteControlEnded() {
    // the RC service resets the zooming state on session close, thus handling both graceful and abandoned
    // sessions. The resetState was moved from the component to the service
    this.controlPanMode(false);
  }

  private onRemotecontrolStarted(needRestore: boolean) {
    if (needRestore) {
      this.restoreState();
    }
    this.doResize(true);

    // with ngIf async, videoOuter or canvasOuter is not available in $onInit()
    const container = this.videoStreaming
      ? document.getElementById('videoOuter')
      : document.getElementById('canvasOuter');

    if (container) {
      const config = {
        attributes: true,
        subtree: true,
        attributeFilter: ['width', 'height'],
      };
      this.mutationObserver.observe(container, config);
    }
  }

  // Run a digest to make sure UI is updated
  private runDigest = () => {
    // if (this.digestDebounce != null) {
    //     clearTimeout(this.digestDebounce);
    // }
    // this.digestDebounce = setTimeout(() => {
    //     // calling $timeout() because $digest() and $apply() run into infinite loop or do not do anything
    //     this.$timeout();
    //     this.digestDebounce = null;
    // }, 100);
  };

  private setAutoZoomActive = (): void => {
    if (this.videoStreaming) {
      const context = this.remoteControlService.getRemoteControlContext();
      const deviceViewHandler = context ? context.deviceViewHandler : null;
      this.autoZoomed = deviceViewHandler ? deviceViewHandler.autoScaleEnabled() : true;
      return;
    }

    this.autoZoomed = this.transformService ? this.transformService.isAutoZoomActive() : true;
  };

  private buildRcMenuButtonItems = () => {
    const zoomInTooltip = this.saI18nPipe.transform('remoteControl_btn_ZoomIn');
    const zoomOutTooltip = this.saI18nPipe.transform('remoteControl_btn_ZoomOut');
    const fitResolutionTooltip = this.saI18nPipe.transform('remoteControl_btn_FitResolution');
    const fitScreenTooltip = this.saI18nPipe.transform('remoteControl_btn_FitScreen');

    this.collapsedButtonItem = {
      name: this.zoomInButtonName,
      type: WorkPanelBarItemType.Button,
      tooltip: () =>
        this.isRecording ? this.saI18nPipe.transform('remoteControl_DisabledWhileRecording_Message') : zoomInTooltip,
      onClick: () => {},
      icon: () => 'hd-icon-zoomin',
      cssClass: () => 'toolbar-popover-list-items',
      isVisible: () => true,
      isDisabled: (): boolean => this.isRecording,
    } as WorkPanelBarItem;

    this.expandedButtonItems = [
      {
        name: this.zoomInButtonName,
        type: WorkPanelBarItemType.Button,
        tooltip: () => zoomInTooltip,
        onClick: () => {
          this.zoom(ScaleSize.SCALE_STEP_UP);
        },
        icon: () => 'hd-icon-zoomin',
        isVisible: () => true,
        isDisabled: (): boolean => !this.canZoom(ScaleSize.SCALE_STEP_UP) || this.isRecording,
      } as WorkPanelBarItem,
      {
        name: this.zoomOutButtonName,
        type: WorkPanelBarItemType.Button,
        tooltip: () => zoomOutTooltip,
        onClick: () => {
          this.zoom(ScaleSize.SCALE_STEP_DOWN);
        },
        icon: () => 'hd-icon-zoomout',
        isVisible: () => true,
        isDisabled: (): boolean => !this.canZoom(ScaleSize.SCALE_STEP_DOWN) || this.isRecording,
      } as WorkPanelBarItem,
      {
        name: this.fitResolutionButtonName,
        type: WorkPanelBarItemType.Button,
        tooltip: () => fitResolutionTooltip,
        onClick: () => {
          this.zoomIdentity();
        },
        icon: () => 'hd-icon-identityzoom',
        isVisible: () => true,
        isDisabled: (): boolean => !this.canZoom(0) || this.isRecording,
      } as WorkPanelBarItem,
      {
        name: this.fitScreenButtonName,
        type: WorkPanelBarItemType.Button,
        tooltip: () => fitScreenTooltip,
        onClick: () => {
          this.zoom(0);
          this.pan(false);
        },
        icon: () => 'hd-icon-resize',
        isVisible: () => true,
        isDisabled: (): boolean => !this.canZoom(0) || this.isRecording,
      } as WorkPanelBarItem,
    ];
  };
}
